import React, { useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

const linkRel = "noreferrer"

const getDefaultPresetId = (presets, defaultPresetId) =>
  defaultPresetId || presets[0]?.id

const Price = ({ className, price, currency }) => {
  if (!price) {
    return null
  }

  return (
    <div className={className}>
      {price}
      {currency ? <span>{currency}</span> : null}
    </div>
  )
}

const ThemeLabels = ({ labels }) => (
  <div className="themes-slider__card-labels">
    {labels.map(label => (
      <span className="themes-slider__card-label" key={label}>
        {label}
      </span>
    ))}
  </div>
)

const ThemePresetCard = ({ card, labels, isPriorityCard }) => {
  const { presets } = card
  const [activePresetId, setActivePresetId] = useState(
    getDefaultPresetId(presets, card.defaultPresetId)
  )

  const cardLabels = card.labels || labels

  return (
    <div className="themes-slider__col" data-theme={card.theme}>
      <div className="themes-slider__card js-themes-slider-card">
        {presets.map(preset => {
          const isActive = preset.id === activePresetId

          if (!isActive) return null

          return (
            <a
              className="themes-slider__card-image-wrapper"
              data-theme-id={preset.id}
              href={preset.image.href}
              key={preset.id}
              target={preset.image.target || "_blank"}
              rel={linkRel}
            >
              <img
                className="themes-slider__card-image"
                src={preset.image.src}
                srcSet={preset.image.srcSet}
                sizes={preset.image.sizes}
                width={preset.image.width}
                height={preset.image.height}
                loading={isPriorityCard ? "eager" : "lazy"}
                fetchpriority={isPriorityCard ? "high" : undefined}
                decoding="async"
                alt={preset.image.alt}
              />

              {preset.badge ? (
                <div className="themes-slider__badge">
                  <div className="themes-slider__new">{preset.badge}</div>
                </div>
              ) : null}
            </a>
          )
        })}

        <div className="themes-slider__card-content">
          <div className="themes-slider__card-header">
            {presets.map(preset => {
              const isActive = preset.id === activePresetId

              return (
                <div
                  className={classNames({ hidden: !isActive })}
                  data-theme-id={preset.id}
                  key={preset.id}
                >
                  <div className="themes-slider__card-top-row">
                    <p className="themes-slider__card-subheading">Theme</p>

                    <Price
                      className="themes-slider__card-price--compared"
                      price={preset.comparedPrice}
                      currency={preset.comparedCurrency}
                    />
                  </div>

                  <div className="themes-slider__card-bottom-row">
                    <a
                      className="themes-slider__card-heading"
                      href={preset.url}
                      target="_blank"
                      rel={linkRel}
                    >
                      {preset.title}
                    </a>

                    <Price
                      className="themes-slider__card-price--regular"
                      price={preset.regularPrice}
                      currency={preset.regularCurrency}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          <div className="themes-slider__card-swatches">
            {presets.map(preset => {
              const isActive = preset.id === activePresetId

              return (
                <button
                  className={classNames(
                    "themes-slider__card-swatch-wrapper",
                    { active: isActive }
                  )}
                  data-theme-id={preset.id}
                  type="button"
                  aria-label={`Show ${preset.title} preset`}
                  aria-pressed={isActive}
                  key={preset.id}
                  onClick={() => setActivePresetId(preset.id)}
                >
                  <span
                    className={classNames(
                      "themes-slider__card-swatch",
                      preset.swatchClass
                    )}
                  />
                </button>
              )
            })}
          </div>

          <ThemeLabels labels={cardLabels} />

          <div className="themes-slider__card-buttons">
            {card.readMore ? (
              <a
                className="themes-slider__card-button button button--secondary button--big"
                href={card.readMore.href}
                target={card.readMore.target || "_blank"}
                rel={linkRel}
                data-theme-read-more=""
              >
                {card.readMore.text}
                <span className="visually-hidden"> about the {card.theme} Shopify theme</span>
              </a>
            ) : null}

            {presets.map(preset => {
              const isActive = preset.id === activePresetId

              return (
                <React.Fragment key={preset.id}>
                  {preset.readMore ? (
                    <a
                      className={classNames(
                        "themes-slider__card-button button button--products-landing button--secondary button--big",
                        { hidden: !isActive }
                      )}
                      href={preset.readMore.href}
                      target={preset.readMore.target || "_blank"}
                      rel={linkRel}
                      data-theme-id={preset.id}
                      data-theme-read-more=""
                    >
                      {preset.readMore.text}
                      <span className="visually-hidden"> about the {card.theme} Shopify theme</span>
                    </a>
                  ) : null}

                  {preset.startTrial ? (
                    <a
                      className={classNames(
                        "themes-slider__card-button button button--products-landing button--primary button--big",
                        { hidden: !isActive }
                      )}
                      href={preset.startTrial.href}
                      target={preset.startTrial.target || "_blank"}
                      rel={linkRel}
                      data-theme-id={preset.id}
                      id={preset.startTrial.id}
                      data-theme-start-trial=""
                    >
                      {preset.startTrial.text}
                    </a>
                  ) : null}
                </React.Fragment>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const ShopifyThemesSlider = ({
  id = "themes-slider",
  heading,
  labels,
  cards,
}) => {
  return (
    <section id={id} className="themes-slider">
      <div className="container container--products-landing">
        <h2 className="themes-slider__heading">{heading}</h2>

        <div className="themes-slider__slider">
          <div className="themes-slider__slider-container">
            <div className="themes-slider__row">
              {cards
                .filter(card => !card.disabled)
                .map((card, index) => (
                  <ThemePresetCard
                    card={card}
                    labels={labels}
                    isPriorityCard={index === 0}
                    key={card.theme}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const buttonShape = PropTypes.shape({
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  target: PropTypes.string,
  id: PropTypes.string,
})

const imageShape = PropTypes.shape({
  href: PropTypes.string,
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string,
  sizes: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  alt: PropTypes.string.isRequired,
  target: PropTypes.string,
})

const pricePropTypes = {
  regularPrice: PropTypes.string.isRequired,
  regularCurrency: PropTypes.string,
  comparedPrice: PropTypes.string,
  comparedCurrency: PropTypes.string,
}

const presetShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  image: imageShape.isRequired,
  swatchClass: PropTypes.string.isRequired,
  badge: PropTypes.string,
  readMore: buttonShape,
  startTrial: buttonShape,
  ...pricePropTypes,
})

const themeCardShape = PropTypes.shape({
  theme: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  defaultPresetId: PropTypes.string,
  readMore: buttonShape,
  presets: PropTypes.arrayOf(presetShape).isRequired,
})

const disabledCardShape = PropTypes.shape({
  disabled: PropTypes.bool.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  image: imageShape.isRequired,
  title: PropTypes.string.isRequired,
  swatchesCount: PropTypes.number.isRequired,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      target: PropTypes.string,
      variant: PropTypes.oneOf(["secondary", "disabled"]).isRequired,
    })
  ).isRequired,
  ...pricePropTypes,
})

Price.propTypes = {
  className: PropTypes.string.isRequired,
  price: PropTypes.string,
  currency: PropTypes.string,
}

ThemeLabels.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
}

ThemePresetCard.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  card: themeCardShape.isRequired,
  isPriorityCard: PropTypes.bool.isRequired,
}

ShopifyThemesSlider.propTypes = {
  id: PropTypes.string,
  heading: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  cards: PropTypes.arrayOf(
    PropTypes.oneOfType([themeCardShape, disabledCardShape])
  ).isRequired,
}

export default ShopifyThemesSlider
